import {
  AgxMultiOptionButton,
  formatPercentage,
} from '@urbanx/agx-ui-components'
import {
  ContractDateToUse,
  ExecutiveMetrics,
  GetMetricsRequest,
  GetMetricsRequestTimePeriod,
} from '@urbanx/agx.rex.client'
import { downloadBlob, getFileNameFromDisposition } from 'helpers'
import { useCallback } from 'react'
import useExportNetworkItemizedTransactions from 'services/functions/rex/reporting/useExportNetworkItemizedTransactions'
import { toExclusiveDate } from 'utils/date'

const downloadCsv = (csvString: string, filename: string) => {
  const blob = new Blob([csvString], { type: 'text/csv' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.setAttribute('hidden', '')
  a.setAttribute('href', url)
  a.setAttribute('download', filename)
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

interface ExportDropdownProps {
  contractDateToUse: ContractDateToUse
  fromDate: string
  networkMetrics: string
  statistics: ExecutiveMetrics[] | undefined
  timePeriod: GetMetricsRequestTimePeriod
  toDateInclusive: string
}

const ExportDropdown = ({
  contractDateToUse,
  fromDate,
  networkMetrics,
  statistics,
  timePeriod,
  toDateInclusive,
}: ExportDropdownProps) => {
  const {
    exportNetworkItemizedTransactions,
    exportNetworkItemizedTransactionsPending,
  } = useExportNetworkItemizedTransactions()

  const handlePDFExport = () => {
    window.print()
  }

  const handleCSVExport = useCallback(() => {
    let filename = 'export.csv'

    if (networkMetrics == 'state') {
      filename = `state.csv`
    } else {
      filename = `partnership.csv`
    }

    const csvString = stateToCsv(statistics)

    downloadCsv(csvString, filename)
  }, [networkMetrics, statistics])

  const handleItemizedExport = useCallback(async () => {
    const request: GetMetricsRequest = {
      contractDateToUse,
      fromDate,
      toDateExclusive: toExclusiveDate(toDateInclusive),
      timePeriod,
    }

    const response = await exportNetworkItemizedTransactions(request)

    const fileName = getFileNameFromDisposition(
      response.headers['content-disposition'],
      'network-itemized-transactions.csv'
    )
    downloadBlob(response.data, fileName)
  }, [
    contractDateToUse,
    exportNetworkItemizedTransactions,
    fromDate,
    timePeriod,
    toDateInclusive,
  ])

  const stateToCsv = useCallback(
    (stateStatistics: ExecutiveMetrics[] | undefined): string => {
      if (!stateStatistics || stateStatistics.length === 0) return ''

      const tableData =
        stateStatistics?.map((stat) => ({
          title: stat?.grouping ?? '0',
          listings: stat?.listings ?? 0,
          contracts: stat?.contracts ?? 0,
          salesVolume: stat?.salesVolume ?? 0,
          avgSalePrice: stat?.avgSalesPrice ?? 0,
          gci: stat?.gci ?? 0,
          avgComm: formatPercentage(stat?.avgCommission?.toString() ?? '0', 2),
          serviceFee: stat?.serviceFee ?? 0,
          avgDom: stat?.avgDOM ?? 0,
          percentNetwork: formatPercentage(
            stat?.percentage?.toString() ?? '0',
            2
          ),
        })) ?? []

      const headers = [
        'State',
        'Listings',
        'Contracts',
        'Sales Volume',
        'AV Sale Price',
        'GCI',
        'AV Comm Rate',
        'Service Fee',
        'Percent Network',
      ]

      const csvRows = [
        headers.join(','),
        ...tableData.map((stat) =>
          [
            stat.title.replaceAll(',', ''),
            stat.listings.toString().replaceAll(',', ''),
            stat.contracts.toString().replaceAll(',', ''),
            stat.salesVolume.toString().replaceAll(',', ''),
            stat.avgSalePrice.toString().replaceAll(',', ''),
            stat.gci.toString().replaceAll(',', ''),
            stat.avgComm.replaceAll(',', ''),
            stat.serviceFee.toString().replaceAll(',', ''),
            stat.percentNetwork.replaceAll(',', ''),
          ].join(',')
        ),
      ].join('\n')

      return csvRows
    },
    []
  )

  return (
    <AgxMultiOptionButton
      primary
      medium
      options={[
        {
          text: 'PDF file',
          onClick: handlePDFExport,
        },
        {
          text: 'CSV file',
          onClick: handleCSVExport,
        },
        {
          text: 'Itemised',
          onClick: handleItemizedExport,
        },
      ]}
      text="Export"
      disabled={exportNetworkItemizedTransactionsPending}
    />
  )
}

export default ExportDropdown
